// src/components/BillingManagement.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { usePermissions } from '../App';
import 'animate.css';
import InvoiceJournalModal from './InvoiceJournalModal';
import BatchBilling from './BatchBilling';
import PreBillReview from './PreBillReview';
import AgedReceivables from './AgedReceivables';
import { collectionsPreviewInvoice, collectionsSendInvoice, collectionsAdvanceInvoice, listDunningLogsForInvoice, getCollectionsSettings } from '../api';
import CollectionsTab from './CollectionsTab';
import UnbilledGenerateButton from './UnbilledGenerateButton';
import InvoiceList from './InvoiceList';
import InvoiceDetails from './InvoiceDetails';
import InvoiceEditor from './InvoiceEditor';
import CashTransactionsReport from './CashTransactionsReport';
import { useConfirm } from './ConfirmProvider';
import { formatCurrency } from '../utils/currency';

function BillingManagement({
  clients,
  cases,
  serviceProducts,
  invoices,
  authAxios,
  fetchInvoices,
  fetchPayments: propFetchPayments, // Rename prop to avoid conflict
  handleDelete,
  invoiceStatusOptions,
  navigateTo,
  setNavigateTo,
  showFormMessage
}) {
  const permissions = usePermissions() || {};
  const confirm = useConfirm();
  // Tab navigation state
  const [activeTab, setActiveTab] = useState('invoices');

  // Tax summary state
  const [taxReport, setTaxReport] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState('');
  const [taxStartDate, setTaxStartDate] = useState('');
  const [taxEndDate, setTaxEndDate] = useState('');

  // WHTB+ (withholding tax) summary state
  const [whtbPlusReport, setWhtbPlusReport] = useState(null);
  const [whtbPlusLoading, setWhtbPlusLoading] = useState(false);
  const [whtbPlusError, setWhtbPlusError] = useState('');
  
  const [showAddInvoiceForm, setShowAddInvoiceForm] = useState(false);
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [invoicesViewMode, setInvoicesViewMode] = useState('tiles');
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false); // State for printing receipt
  const [isLoadingDetails, setIsLoadingDetails] = useState(false); // For loading state in details modal


  const showToast = useCallback((message, type = 'success') => {
    if (typeof showFormMessage === 'function') {
      showFormMessage(type, message);
      return;
    }
    console.warn('Toast:', type, message);
  }, [showFormMessage]);
  const [newInvoice, setNewInvoice] = useState({
    client: '', case: '', issue_date: '', due_date: '', status: 'Draft',
    invoice_items: [],
  });
  const [newItem, setNewItem] = useState({
    service_product: '', quantity: 1, unit_price: '', description: '',
  });
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'BANK_TRANSFER', notes: ''
  });
  // New: journal modal state
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalMode, setJournalMode] = useState('payment'); // 'payment' | 'write_off'
  const [journalInvoice, setJournalInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState(null);
  const [newEditItem, setNewEditItem] = useState({
    service_product: '', description: '', quantity: 1, unit_price: 0.00,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Restore saved view preferences from localStorage
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem('billing.ui') || '{}');
      if (p.viewMode) setInvoicesViewMode(p.viewMode);
      if (typeof p.statusFilter === 'string') setStatusFilter(p.statusFilter);
    } catch (_) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('billing.ui', JSON.stringify({ viewMode: invoicesViewMode, statusFilter })); } catch (_) {}
  }, [invoicesViewMode, statusFilter]);
  const [payments, setPayments] = useState([]);
  const [paidLast30Days, setPaidLast30Days] = useState(0);
  const [filteredCases, setFilteredCases] = useState([]);
  // Unbilled WIP state
  const [unbilledByClient, setUnbilledByClient] = useState({});
  const [showUnbilled, setShowUnbilled] = useState(true);

  // State for new payment modals
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [selectedPaymentForDetails, setSelectedPaymentForDetails] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [detailsTab, setDetailsTab] = useState('items');
  // Collections preview state inside details panel
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // State for expanded unbilled items
  const [expandedClients, setExpandedClients] = useState({});
  // Checkbox selection for individual WIP entries
  const [selectedEntries, setSelectedEntries] = useState({ time: {}, expense: {}, fee: {} });

  // Toggle expansion for a client's unbilled items
  const toggleClientExpansion = (clientName) => {
    setExpandedClients(prev => ({ ...prev, [clientName]: !prev[clientName] }));
  };

  const toggleEntry = (type, id) => {
    setSelectedEntries(prev => ({
      ...prev,
      [type]: { ...prev[type], [id]: !prev[type][id] }
    }));
  };

  const toggleAllForClient = (clientName, buckets) => {
    const allIds = [
      ...(buckets.time_entries || []).map(e => ['time', e.id]),
      ...(buckets.expense_entries || []).map(e => ['expense', e.id]),
      ...(buckets.fee_entries || []).map(e => ['fee', e.id]),
    ];
    const allSelected = allIds.every(([type, id]) => selectedEntries[type][id]);
    setSelectedEntries(prev => {
      const next = { time: { ...prev.time }, expense: { ...prev.expense }, fee: { ...prev.fee } };
      allIds.forEach(([type, id]) => { next[type][id] = !allSelected; });
      return next;
    });
  };

  const getSelectedIdsForClient = (buckets) => {
    const timeIds = (buckets.time_entries || []).filter(e => selectedEntries.time[e.id]).map(e => e.id);
    const expenseIds = (buckets.expense_entries || []).filter(e => selectedEntries.expense[e.id]).map(e => e.id);
    const feeIds = (buckets.fee_entries || []).filter(e => selectedEntries.fee[e.id]).map(e => e.id);
    return { timeIds, expenseIds, feeIds, hasSelection: timeIds.length + expenseIds.length + feeIds.length > 0 };
  };

  // Delete individual unbilled time entry
  const handleDeleteTimeEntry = async (entryId) => {
    const ok = await confirm({
      title: 'Confirm deletion',
      message: 'Delete this time entry? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await authAxios.delete(`/time-entries/${entryId}/`);
      showToast('Time entry deleted successfully', 'success');
      await fetchUnbilled();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      showToast('Failed to delete time entry', 'danger');
    }
  };

  // Delete individual unbilled disbursement
  const handleDeleteExpenseEntry = async (entryId) => {
    const ok = await confirm({
      title: 'Confirm deletion',
      message: 'Delete this disbursement? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await authAxios.delete(`/expense-entries/${entryId}/`);
      showToast('Disbursement deleted successfully', 'success');
      await fetchUnbilled();
    } catch (error) {
      console.error('Error deleting disbursement:', error);
      showToast('Failed to delete disbursement', 'danger');
    }
  };

  // Delete individual unbilled debt recovery fee entry
  const handleDeleteFeeEntry = async (entryId) => {
    const ok = await confirm({
      title: 'Confirm deletion',
      message: 'Delete this fee entry? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await authAxios.delete(`/debt-recovery-fee-entries/${entryId}/`);
      showToast('Fee entry deleted successfully', 'success');
      await fetchUnbilled();
    } catch (error) {
      console.error('Error deleting fee entry:', error);
      showToast('Failed to delete fee entry', 'danger');
    }
  };

  // Define fetch functions first before using them in callbacks
  const fetchPayments = async () => {
    try {
        const response = await authAxios.get('/payments/');
        const fetchedPayments = response.data.results || response.data;
        setPayments(fetchedPayments);
        return fetchedPayments;
    } catch (error) {
        console.error("Failed to fetch payments", error);
        return [];
    }
  };

  const fetchUnbilled = useCallback(async () => {
    try {
      const resp = await authAxios.get('/invoices/unbilled_activity/');
      const data = resp.data?.results || {};
      setUnbilledByClient(data);
    } catch (e) {
      console.error('Failed to load unbilled activity', e);
    }
  }, [authAxios]);

  // Handler specifically for after invoice generation from WIP
  const handleInvoiceGenerated = useCallback(async () => {
    console.log('Invoice generated - refreshing data...');
    console.log('Current invoices count before refresh:', invoices.length);
    try {
      await Promise.all([
        fetchInvoices(),
        fetchUnbilled(),
        fetchPayments()
      ]);
      console.log('Data refresh complete after invoice generation');
      // Log invoices count after a brief delay to allow state to update
      setTimeout(() => {
        console.log('Invoices count after refresh (delayed check):', invoices.length);
      }, 500);
    } catch (error) {
      console.error('Error refreshing data after invoice generation:', error);
    }
  }, [fetchInvoices, fetchUnbilled, authAxios, invoices.length]);

  useEffect(() => {
    fetchPayments();
  }, [authAxios]);

  // Support query-string tab deep links, e.g. /billing?tab=tax
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) setActiveTab(tab);
    } catch (_e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchUnbilled();
  }, [fetchUnbilled]);

  // Helper to format date as YYYY-MM-DD in local time
  const formatYmd = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Use shared formatCurrency from utils/currency

  const fetchTaxReport = useCallback(async () => {
    setTaxLoading(true);
    setTaxError('');
    try {
      const params = {};
      if (taxStartDate) params.start_date = taxStartDate;
      if (taxEndDate) params.end_date = taxEndDate;
      const resp = await authAxios.get('/reports/tax/', { params });
      setTaxReport(resp.data);
      return resp.data;
    } catch (err) {
      console.error('Failed to fetch tax report', err);
      setTaxError('Failed to load tax summary.');
      setTaxReport(null);
      return null;
    } finally {
      setTaxLoading(false);
    }
  }, [authAxios, taxStartDate, taxEndDate]);

  const fetchWhtbPlusReport = useCallback(async () => {
    setWhtbPlusLoading(true);
    setWhtbPlusError('');
    try {
      const params = {};
      if (taxStartDate) params.start_date = taxStartDate;
      if (taxEndDate) params.end_date = taxEndDate;
      const resp = await authAxios.get('/reports/whtb-plus/', { params });
      setWhtbPlusReport(resp.data);
      return resp.data;
    } catch (err) {
      console.error('Failed to fetch WHTB+ report', err);
      setWhtbPlusError('Failed to load WHTB+ summary.');
      setWhtbPlusReport(null);
      return null;
    } finally {
      setWhtbPlusLoading(false);
    }
  }, [authAxios, taxStartDate, taxEndDate]);

  const downloadWhtbPlusPdf = useCallback(async () => {
    try {
      const params = {};
      if (taxStartDate) params.start_date = taxStartDate;
      if (taxEndDate) params.end_date = taxEndDate;

      const resp = await authAxios.get('/reports/whtb-plus/pdf/', {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      const suffix = (taxStartDate || taxEndDate) ? `_${taxStartDate || 'start'}_${taxEndDate || 'end'}` : '';
      a.download = `WHTBPlus${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download WHTB+ PDF', err);
      showToast('Failed to download WHTB+ PDF.', 'danger');
    }
  }, [authAxios, taxStartDate, taxEndDate, showToast]);

  useEffect(() => {
    if (activeTab === 'tax') {
      fetchTaxReport();
      fetchWhtbPlusReport();
    }
  }, [activeTab, fetchTaxReport, fetchWhtbPlusReport]);

  // Fetch Total Paid (all-time) using Financial Reports endpoint (journal-based)
  const fetchTotalPaid = useCallback(async () => {
    try {
      const resp = await authAxios.get('/reports/financial/');
      const amt = parseFloat(resp.data?.paid_invoices_amount || 0) || 0;
      setPaidLast30Days(amt);
      return amt;
    } catch (err) {
      console.error('Failed to fetch total paid from financial reports', err);
      setPaidLast30Days(0);
      return 0;
    }
  }, [authAxios]);

  // Initial and reactive fetch for all-time paid figure
  useEffect(() => {
    fetchTotalPaid();
    // Recompute when invoices or payments change to keep card fresh after actions
  }, [fetchTotalPaid, invoices, payments]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const client = clients.find(c => c.id === invoice.client);
      const clientName = client ? (client.first_name ? `${client.first_name} ${client.last_name}` : client.organization_name) : '';
      const caseTitle = cases.find(c => c.id === invoice.case)?.title || '';
      
      const matchesSearchTerm = searchTerm === '' ||
        invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        caseTitle.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === '' || invoice.status === statusFilter;
      const matchesClient = clientFilter === '' || invoice.client === parseInt(clientFilter);
      
      const issueDate = new Date(invoice.issue_date);
      const matchesStartDate = startDateFilter === '' || issueDate >= new Date(startDateFilter);
      const matchesEndDate = endDateFilter === '' || issueDate <= new Date(endDateFilter);

      return matchesSearchTerm && matchesStatus && matchesClient && matchesStartDate && matchesEndDate;
    });
  }, [invoices, searchTerm, statusFilter, clientFilter, startDateFilter, endDateFilter, clients, cases]);

  const financialOverview = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Use balance_due from the API for accuracy
    const totalOutstanding = invoices
        .filter(inv => ['Sent', 'Overdue'].includes(inv.status))
        .reduce((acc, inv) => acc + (parseFloat(inv.balance_due) || 0), 0);
    
  // Use backend journal-based reports for accurate last-30-days paid
  const totalPaidLast30Days = paidLast30Days;
        
    const overdueCount = invoices.filter(inv => inv.status === 'Overdue').length;

    return {
        totalOutstanding,
        totalPaidLast30Days,
        overdueCount,
    };
  }, [invoices, paidLast30Days]);


  // --- Payment Modal Handlers ---
  const openPaymentDetailsModal = (payment) => {
    setSelectedPaymentForDetails(payment);
    setShowPaymentDetailsModal(true);
  };
  const closePaymentDetailsModal = () => setShowPaymentDetailsModal(false);

  const openEditPaymentModal = (payment) => {
    setEditingPayment({ ...payment });
    setShowEditPaymentModal(true);
  };
  const closeEditPaymentModal = () => setEditingPayment(null);

  const handleEditPaymentChange = (e) => {
    const { name, value } = e.target;
    setEditingPayment(prev => ({ ...prev, [name]: value }));
  };

  const handleEditPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!editingPayment) return;
    setIsSaving(true);
    try {
      await authAxios.put(`/payments/${editingPayment.id}/`, editingPayment);
      showToast('Payment updated successfully!', 'success');
      closeEditPaymentModal();
      
      // Refetch all data to ensure consistency
      fetchInvoices();
      const updatedPayments = await fetchPayments();

      // If the invoice details modal is open, refresh its data
      if (selectedInvoiceForDetails) {
        const invoiceResponse = await authAxios.get(`/invoices/${selectedInvoiceForDetails.id}/`);
        const updatedInvoiceDetails = invoiceResponse.data;
        updatedInvoiceDetails.payments = updatedPayments.filter(p => p.invoice === selectedInvoiceForDetails.id);
        setSelectedInvoiceForDetails(updatedInvoiceDetails);
      }
    } catch (error) {
      console.error('Error updating payment:', error.response ? error.response.data : error);
      showToast(`Error updating payment: ${error.response?.data?.detail || 'Please check your input.'}`, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    const ok = await confirm({
      title: 'Delete payment?',
      message: 'Are you sure you want to delete this payment?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await authAxios.delete(`/payments/${paymentId}/`);
      showToast('Payment deleted successfully!', 'success');
      fetchInvoices();
      const updatedPayments = await fetchPayments();

      // Refresh invoice details if modal is open
      if (selectedInvoiceForDetails) {
        const invoiceResponse = await authAxios.get(`/invoices/${selectedInvoiceForDetails.id}/`);
        const updatedInvoiceDetails = invoiceResponse.data;
        updatedInvoiceDetails.payments = updatedPayments.filter(p => p.invoice === selectedInvoiceForDetails.id);
        setSelectedInvoiceForDetails(updatedInvoiceDetails);
      }
    } catch (error) {
      console.error('Error deleting payment:', error.response ? error.response.data : error);
      showToast('Failed to delete payment.', 'danger');
    }
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInvoice = { ...newInvoice, [name]: value };

    if (name === 'client') {
      if (value) {
        setFilteredCases(cases.filter(c => c.client === parseInt(value)));
        updatedInvoice.case = ''; // Reset case selection
      } else {
        setFilteredCases([]);
      }
    }
    setNewInvoice(updatedInvoice);
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prevItem => {
      let updatedValue = value;
      let updatedUnitPrice = prevItem.unit_price;
      let updatedDescription = prevItem.description;

      if (name === 'service_product') {
        const selectedServiceProduct = serviceProducts.find(sp => sp.id === parseInt(value));
        if (selectedServiceProduct) {
          updatedUnitPrice = parseFloat(selectedServiceProduct.price);
          updatedDescription = selectedServiceProduct.name;
        } else {
          updatedUnitPrice = '';
          updatedDescription = '';
        }
      } else if (name === 'unit_price') {
        updatedUnitPrice = value;
      } else if (name === 'description') {
        updatedDescription = value;
      }

      return {
        ...prevItem,
        [name]: updatedValue,
        unit_price: updatedUnitPrice,
        description: updatedDescription,
      };
    });
  };

  const addInvoiceItem = () => {
    if (newItem.service_product || newItem.description) {
      const unitPrice = parseFloat(newItem.unit_price);
      if (isNaN(unitPrice) || unitPrice <= 0) {
        showToast('Unit Price must be a positive number.', 'danger');
        return;
      }

      const serviceProduct = serviceProducts.find(sp => sp.id === parseInt(newItem.service_product));
      const itemToAdd = {
        service_product: newItem.service_product ? parseInt(newItem.service_product) : null,
        description: newItem.description || (serviceProduct ? serviceProduct.name : 'Custom Item'),
        quantity: parseFloat(newItem.quantity),
        unit_price: unitPrice,
      };
      setNewInvoice({
        ...newInvoice,
        invoice_items: [...newInvoice.invoice_items, itemToAdd],
      });
      setNewItem({ service_product: '', quantity: 1, unit_price: '', description: '' });
    } else {
      showToast('Please select a service/product or provide a description.', 'warning');
    }
  };

  const removeInvoiceItem = (indexToRemove) => {
    setNewInvoice({
      ...newInvoice,
      invoice_items: newInvoice.invoice_items.filter((_, index) => index !== indexToRemove),
    });
  };

  const handleAddInvoiceSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const { invoice_items: _ignoredCreateItems, ...restCreate } = newInvoice;
      const payload = {
        ...restCreate,
        client: parseInt(newInvoice.client),
        case: newInvoice.case ? parseInt(newInvoice.case) : null,
        items: (newInvoice.invoice_items || []).map(item => ({
            ...item,
            service_product: item.service_product?.id || item.service_product || null,
        })),
      };
      
      await authAxios.post('/invoices/', payload);

      showToast('Invoice added successfully!', 'success');
      setShowAddInvoiceForm(false);
      setNewInvoice({ client: '', case: '', issue_date: '', due_date: '', status: 'Draft', invoice_items: [] });
      setFilteredCases([]);
      fetchInvoices();
    } catch (error) {
      console.error('Error adding invoice:', error.response ? error.response.data : error);
      let errorMessage = 'Please check your input.';
      if (error.response?.data) {
        const errorData = error.response.data;
        try {
          const specificErrors = Object.entries(errorData).map(([key, value]) => {
            if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
            if (typeof value === 'object' && value !== null) return `${key}: ${Object.values(value).join(', ')}`;
            return `${key}: ${String(value)}`;
          }).join('; ');
          if (specificErrors) errorMessage = specificErrors;
        } catch (_e) {
          errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
        }
      }
      showToast(`Error adding invoice: ${errorMessage}`, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditInvoiceModal = async (invoice) => {
    try {
      const response = await authAxios.get(`/invoices/${invoice.id}/`);
      const invoiceDetails = response.data;
      if (invoiceDetails.client) {
          setFilteredCases(cases.filter(c => c.client === invoiceDetails.client));
      }
      // Ensure invoice_items is an array for safe rendering
      invoiceDetails.invoice_items = invoiceDetails.invoice_items || [];
      setEditingInvoice(invoiceDetails);
      setShowEditInvoiceModal(true);
    } catch (error) {
      console.error('Error fetching invoice for edit:', error.response ? error.response.data : error);
      showToast(`Error fetching invoice details: ${error.response?.data?.detail || 'Please try again.'}`, 'danger');
    }
  };

  // CollectionsTab has been extracted to ./CollectionsTab.js

  const closeEditInvoiceModal = () => {
    setEditingInvoice(null);
    setShowEditInvoiceModal(false);
    setNewEditItem({ service_product: '', description: '', quantity: 1, unit_price: 0.00 });
    setFilteredCases([]);
  };

  const handleEditInvoiceChange = (e) => {
    const { name, value } = e.target;
    const updatedInvoice = { ...editingInvoice, [name]: value };

    if (name === 'client' && value) {
        setFilteredCases(cases.filter(c => c.client === parseInt(value)));
        updatedInvoice.case = ''; // Reset case selection
    }
    setEditingInvoice(updatedInvoice);
  };

  const handleEditInvoiceSubmit = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;
  
    setIsSaving(true);
    try {
      const { invoice_items: _ignoredEditItems, ...restEdit } = editingInvoice;
      const payload = {
        ...restEdit,
        items: (editingInvoice.invoice_items || []).map(item => ({
          ...item,
          service_product: item.service_product?.id || item.service_product || null,
        })),
      };
      
      await authAxios.put(`/invoices/${editingInvoice.id}/`, payload, { headers: { 'Content-Type': 'application/json' } });
  
      showToast(`Invoice #${editingInvoice.invoice_number} updated successfully!`, 'success');
      closeEditInvoiceModal();
      fetchInvoices(); 
    } catch (error) {
      console.error('Error updating invoice:', error.response ? error.response.data : error);
      let errorMessage = 'Please check your input.';
      if (error.response?.data) {
        const errorData = error.response.data;
        const specificErrors = Object.entries(errorData).map(([key, value]) => {
          const messages = Array.isArray(value) ? value.join(', ') : String(value);
          return `${key}: ${messages}`;
        }).join('; ');

        if (specificErrors) {
          errorMessage = specificErrors;
        }
      }
      showToast(`Error updating invoice: ${errorMessage}`, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewEditItemChange = (e) => {
    const { name, value } = e.target;
    setNewEditItem(prev => {
        let updatedState = { ...prev, [name]: value };

        if (name === 'service_product') {
            const selectedService = serviceProducts.find(sp => sp.id === parseInt(value));
            if (selectedService) {
                updatedState.description = selectedService.name;
                updatedState.unit_price = parseFloat(selectedService.price);
            } else {
                updatedState.description = '';
                updatedState.unit_price = 0.00;
            }
        }
        return updatedState;
    });
  };

  const addEditedInvoiceItem = () => {
    if (newEditItem.description && newEditItem.quantity > 0 && newEditItem.unit_price >= 0) {
      setEditingInvoice(prev => ({
        ...prev,
        invoice_items: [...(prev.invoice_items || []), {
          ...newEditItem,
          id: null, 
          service_product: newEditItem.service_product ? parseInt(newEditItem.service_product, 10) : null,
          quantity: parseFloat(newEditItem.quantity),
          unit_price: parseFloat(newEditItem.unit_price),
        }],
      }));
      setNewEditItem({ service_product: '', description: '', quantity: 1, unit_price: 0.00 });
    } else {
      showToast('Please provide a description, quantity, and a valid unit price.', 'danger');
    }
  };

  const removeEditedInvoiceItem = (indexToRemove) => {
    setEditingInvoice(prev => ({
      ...prev,
      invoice_items: prev.invoice_items.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleEditInvoiceItemFieldChange = (index, e) => {
    const { name, value } = e.target;
    setEditingInvoice(prev => {
      const updatedItems = [...(prev.invoice_items || [])];
      let currentItem = { ...updatedItems[index] };
      
      currentItem[name] = value;

      if (name === 'service_product') {
        const selectedService = serviceProducts.find(sp => sp.id === parseInt(value));
        if (selectedService) {
            currentItem.description = selectedService.name;
            currentItem.unit_price = parseFloat(selectedService.price);
        } else {
            currentItem.service_product = null;
        }
      }
      
      updatedItems[index] = currentItem;
      return { ...prev, invoice_items: updatedItems };
    });
  };

  const openDetailsModal = useCallback(async (invoice) => {
    setIsLoadingDetails(true);
    try {
      const response = await authAxios.get(`/invoices/${invoice.id}/`);
      const invoiceDetails = response.data;
      // journals are expected in invoiceDetails.journals
      setSelectedInvoiceForDetails(invoiceDetails);
      setShowDetailsModal(true);
      setDetailsTab('items');
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      showToast('Failed to fetch invoice details.', 'danger');
    } finally {
        setIsLoadingDetails(false);
    }
  }, [authAxios, showToast]);

  const closeDetailsModal = () => {
    setSelectedInvoiceForDetails(null);
    setShowDetailsModal(false);
  };

  const openRecordPaymentModal = (invoice) => {
    setJournalInvoice(invoice);
    setJournalMode('payment');
    setShowJournalModal(true);
  };

  const openWriteOffModal = (invoice) => {
    setJournalInvoice(invoice);
    setJournalMode('write_off');
    setShowJournalModal(true);
  };

  const openCreditNoteModal = (invoice) => {
    setJournalInvoice(invoice);
    setJournalMode('credit');
    setShowJournalModal(true);
  };

  const openRefundModal = (invoice) => {
    setJournalInvoice(invoice);
    setJournalMode('refund');
    setShowJournalModal(true);
  };

  const closeRecordPaymentModal = () => {
    setShowRecordPaymentModal(false);
    setSelectedInvoiceForPayment(null);
    setNewPayment({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'BANK_TRANSFER',
      notes: '',
    });
  };

  const handleRecordPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInvoiceForPayment) return;
    setIsSaving(true);
    try {
      const paymentDataWithInvoice = {
        ...newPayment,
        invoice: selectedInvoiceForPayment.id,
        amount: parseFloat(newPayment.amount),
      };
      await authAxios.post('/payments/', paymentDataWithInvoice);
      showToast('Payment recorded successfully!', 'success');
      closeRecordPaymentModal();
      fetchInvoices();
      if (propFetchPayments) propFetchPayments();
      fetchPayments();
    } catch (error) {
      console.error('Error recording payment:', error.response ? error.response.data : error);
      showToast(`Error recording payment: ${error.response?.data?.detail || 'Please check your input.'}`, 'danger');
    } finally {
        setIsSaving(false);
    }
  };

  const handleGeneratePdf = async (invoiceId) => {
    try {
      const response = await authAxios.get(`/invoices/${invoiceId}/generate_pdf/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Invoice PDF generated and downloaded!', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error.response ? error.response.data : error);
      showToast(`Error generating PDF: ${error.response?.data?.detail || 'Failed to generate PDF.'}`, 'danger');
    }
  };

  const handlePrintReceipt = async (paymentId) => {
    setIsPrinting(true);
    try {
      const response = await authAxios.get(`/payments/${paymentId}/generate_receipt/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast('Payment receipt downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error generating receipt:', error.response ? error.response.data : error);
      showToast('Failed to generate payment receipt.', 'danger');
    } finally {
      setIsPrinting(false);
    }
  };

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'billing') {
      const invoice = invoices.find(i => i.id === navigateTo.id);
      if (invoice) {
        openDetailsModal(invoice);
        setNavigateTo(null);
      }
    }
  }, [navigateTo, invoices, setNavigateTo, openDetailsModal]);

  // Auto-open via custom event (open-resource-detail)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'billing') return;
      const invoice = invoices.find(i => i.id === detail.id);
      if (invoice) {
        openDetailsModal(invoice);
        if (setNavigateTo) setNavigateTo(null);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [invoices, openDetailsModal, setNavigateTo]);


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Local toast UI removed in favor of global toasts (showFormMessage). */}
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Billing Management</h2>
      
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoices
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'batch'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Batch Billing
            </button>
            <button
              onClick={() => setActiveTab('prebill')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'prebill'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pre-Bill Review
            </button>
            <button
              onClick={() => setActiveTab('receivables')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'receivables'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Aged Receivables
            </button>

            <button
              onClick={() => setActiveTab('cash-report')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cash-report'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cash Transactions Report
            </button>

            <button
              onClick={() => setActiveTab('tax')}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tax'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tax Summary
            </button>
          </nav>
        </div>
      </div>
      
      {/* Batch Billing Tab Content */}
      {activeTab === 'batch' && (
        <BatchBilling
          authAxios={authAxios}
          showToast={showToast}
          fetchInvoices={fetchInvoices}
        />
      )}

      {/* Pre-Bill Review Tab Content */}
      {activeTab === 'prebill' && (
        <PreBillReview
          authAxios={authAxios}
          showToast={showToast}
          fetchInvoices={fetchInvoices}
          permissions={permissions}
        />
      )}

      {/* Invoices Tab Content */}
      {activeTab === 'invoices' && (
        <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-600">Total Outstanding</h3>
            <p className="text-3xl font-bold text-red-600 mt-2">P{financialOverview.totalOutstanding.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-600">Total Paid</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">P{financialOverview.totalPaidLast30Days.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-600">Overdue Invoices</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">{financialOverview.overdueCount}</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-800">Unbilled Activity</h3>
          <button className="text-sm text-indigo-600 hover:underline" onClick={() => setShowUnbilled(v => !v)}>{showUnbilled ? 'Hide' : 'Show'}</button>
        </div>
        {showUnbilled && (
          <div className="mt-4 space-y-4">
            {Object.keys(unbilledByClient).length === 0 ? (
              <p className="text-gray-500">No unbilled time or expenses.</p>
            ) : (
              Object.entries(unbilledByClient).map(([clientName, buckets]) => {
                const timeCount = (buckets.time_entries || []).length;
                const expCount = (buckets.expense_entries || []).length;
                const feeCount = (buckets.fee_entries || []).length;
                
                // Try to get client_id from any available entry
                let clientId = null;
                if (buckets.time_entries && buckets.time_entries.length > 0) {
                  clientId = buckets.time_entries[0].client_id;
                } else if (buckets.expense_entries && buckets.expense_entries.length > 0) {
                  clientId = buckets.expense_entries[0].client_id;
                } else if (buckets.fee_entries && buckets.fee_entries.length > 0) {
                  clientId = buckets.fee_entries[0].client_id;
                }
                
                // Log if clientId is missing for debugging
                if (!clientId) {
                  console.warn('Missing client_id for unbilled activity:', clientName, buckets);
                }
                
                return (
                  <div key={clientName} className="border rounded-md p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-medium text-gray-800">{clientName}</h4>
                          <button
                            onClick={() => toggleClientExpansion(clientName)}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            {expandedClients[clientName] ? '▼' : '▶'} Details
                          </button>
                        </div>
                        <p className="text-sm text-gray-600">{timeCount} time entries, {expCount} expenses, {feeCount} fees ready to bill</p>
                        {!clientId && <p className="text-xs text-red-500 mt-1">⚠️ Client ID missing - cannot generate invoice</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const sel = getSelectedIdsForClient(buckets);
                          return (
                            <UnbilledGenerateButton
                              clientId={clientId}
                              timeEntryIds={sel.hasSelection ? sel.timeIds : (buckets.time_entries || []).map(t => t.id)}
                              expenseEntryIds={sel.hasSelection ? sel.expenseIds : (buckets.expense_entries || []).map(e => e.id)}
                              feeEntryIds={sel.hasSelection ? sel.feeIds : (buckets.fee_entries || []).map(f => f.id)}
                              authAxios={authAxios}
                              onInvoiceGenerated={() => { setSelectedEntries({ time: {}, expense: {}, fee: {} }); handleInvoiceGenerated(); }}
                              showToast={showToast}
                            />
                          );
                        })()}
                      </div>
                    </div>
                    
                    {/* Expandable details section */}
                    {expandedClients[clientName] && (
                      <div className="mt-4 space-y-3 border-t pt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                            <input type="checkbox"
                              checked={[...(buckets.time_entries || []), ...(buckets.expense_entries || []), ...(buckets.fee_entries || [])].every(e => {
                                const type = buckets.time_entries?.includes(e) ? 'time' : buckets.expense_entries?.includes(e) ? 'expense' : 'fee';
                                return selectedEntries[type][e.id];
                              })}
                              onChange={() => toggleAllForClient(clientName, buckets)}
                            />
                            Select all for invoicing
                          </label>
                          {(() => {
                            const sel = getSelectedIdsForClient(buckets);
                            if (!sel.hasSelection) return null;
                            return <span className="text-xs text-indigo-600">{sel.timeIds.length + sel.expenseIds.length + sel.feeIds.length} items selected</span>;
                          })()}
                        </div>
                        {/* Time Entries */}
                        {buckets.time_entries && buckets.time_entries.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Time Entries:</h5>
                            <div className="space-y-2">
                              {buckets.time_entries.map(entry => {
                                const rate = Number(entry.billing_rate) || 0;
                                const hours = Number(entry.hours || entry.duration_hours) || 0;
                                const amount = rate * hours;
                                return (
                                <div key={entry.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={!!selectedEntries.time[entry.id]} onChange={() => toggleEntry('time', entry.id)} />
                                  </label>
                                  <div className="flex-1 ml-2">
                                    <p className="font-medium">{entry.service_product || 'Billable Time'}</p>
                                    <p className="text-gray-600">{entry.description}</p>
                                    <p className="text-gray-500 text-xs">
                                      {hours}h @ {formatCurrency(rate)}/hr = <span className="font-semibold text-gray-700">{formatCurrency(amount)}</span> • Case: {entry.case || 'N/A'}
                                      {entry.billing_status && entry.billing_status !== 'UNBILLED' && (
                                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${entry.billing_status === 'BILLED' ? 'bg-green-100 text-green-700' : entry.billing_status === 'WRITTEN_OFF' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{entry.billing_status}</span>
                                      )}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteTimeEntry(entry.id)}
                                    className="ml-2 px-2 py-1 text-xs text-red-600 hover:text-red-900 hover:bg-red-50 rounded flex items-center"
                                    title="Delete this time entry"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                    Delete
                                  </button>
                                </div>
                              );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Disbursements */}
                        {buckets.expense_entries && buckets.expense_entries.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Disbursements:</h5>
                            <div className="space-y-2">
                              {buckets.expense_entries.map(entry => (
                                <div key={entry.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={!!selectedEntries.expense[entry.id]} onChange={() => toggleEntry('expense', entry.id)} />
                                  </label>
                                  <div className="flex-1 ml-2">
                                    <p className="font-medium">{entry.category || 'Expense'}</p>
                                    <p className="text-gray-600">{entry.description}</p>
                                    <p className="text-gray-500 text-xs">
                                      {formatCurrency(entry.amount)} • Case: {entry.case || 'N/A'}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteExpenseEntry(entry.id)}
                                    className="ml-2 px-2 py-1 text-xs text-red-600 hover:text-red-900 hover:bg-red-50 rounded flex items-center"
                                    title="Delete this disbursement"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                    Delete
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Fee Entries */}
                        {buckets.fee_entries && buckets.fee_entries.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Fee Entries:</h5>
                            <div className="space-y-2">
                              {buckets.fee_entries.map(entry => (
                                <div key={entry.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={!!selectedEntries.fee[entry.id]} onChange={() => toggleEntry('fee', entry.id)} />
                                  </label>
                                  <div className="flex-1 ml-2">
                                    <p className="font-medium">{entry.fee_type || 'Fee'} {entry.request_reference ? `• ${entry.request_reference}` : ''}</p>
                                    <p className="text-gray-600">{entry.description || 'Debt recovery fee'}</p>
                                    <p className="text-gray-500 text-xs">
                                      {formatCurrency(entry.amount)}
                                      {entry.case ? ` • Case: ${entry.case}` : ''}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteFeeEntry(entry.id)}
                                    className="ml-2 px-2 py-1 text-xs text-red-600 hover:text-red-900 hover:bg-red-50 rounded flex items-center"
                                    title="Delete this fee entry"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                    Delete
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

       <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input type="text" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search Invoices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {invoiceStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || c.organization_name}</option>)}
            </select>
            <input type="date" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={startDateFilter} onChange={e => setStartDateFilter(e.target.value)} />
            <input type="date" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={endDateFilter} onChange={e => setEndDateFilter(e.target.value)} />
        </div>
      </div>


      <div className="flex justify-between items-center mb-6">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
          onClick={() => setShowAddInvoiceForm(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Invoice
        </button>
        <div className="flex space-x-2">
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${invoicesViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setInvoicesViewMode('list')}>
             List
          </button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${invoicesViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setInvoicesViewMode('tiles')}>
             Tiles
          </button>
        </div>
      </div>
      
      {showAddInvoiceForm && (
        <InvoiceEditor
          mode="create"
          clients={clients}
          cases={cases}
          filteredCases={filteredCases}
          setFilteredCases={setFilteredCases}
          serviceProducts={serviceProducts}
          isSaving={isSaving}
          onCancel={() => setShowAddInvoiceForm(false)}
          onSubmit={async (payload) => {
            setIsSaving(true);
            try {
              await authAxios.post('/invoices/', payload);
              showToast('Invoice added successfully!', 'success');
              setShowAddInvoiceForm(false);
              setFilteredCases([]);
              fetchInvoices();
            } catch (error) {
              console.error('Error adding invoice:', error.response ? error.response.data : error);
              let errorMessage = 'Please check your input.';
              if (error.response?.data) {
                const errorData = error.response.data;
                try {
                  const specificErrors = Object.entries(errorData).map(([key, value]) => {
                    if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
                    if (typeof value === 'object' && value !== null) return `${key}: ${Object.values(value).join(', ')}`;
                    return `${key}: ${String(value)}`;
                  }).join('; ');
                  if (specificErrors) errorMessage = specificErrors;
                } catch (_e) {
                  errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
                }
              }
              showToast(`Error adding invoice: ${errorMessage}`, 'danger');
            } finally {
              setIsSaving(false);
            }
          }}
        />
      )}

      <InvoiceList
        filteredInvoices={filteredInvoices}
        invoicesViewMode={invoicesViewMode}
        setInvoicesViewMode={setInvoicesViewMode}
        clients={clients}
        cases={cases}
        openEditInvoiceModal={openEditInvoiceModal}
        openDetailsModal={openDetailsModal}
        openRecordPaymentModal={openRecordPaymentModal}
        openWriteOffModal={openWriteOffModal}
        handleGeneratePdf={handleGeneratePdf}
        handleDelete={handleDelete}
        fetchInvoices={fetchInvoices}
      />
      
      {showEditInvoiceModal && editingInvoice && (
        <InvoiceEditor
          mode="edit"
          initialInvoice={editingInvoice}
          clients={clients}
          cases={cases}
          filteredCases={filteredCases}
          setFilteredCases={setFilteredCases}
          serviceProducts={serviceProducts}
          invoiceStatusOptions={invoiceStatusOptions}
          isSaving={isSaving}
          onCancel={closeEditInvoiceModal}
          onSubmit={async (payload) => {
            if (!editingInvoice) return;
            setIsSaving(true);
            try {
              await authAxios.put(`/invoices/${editingInvoice.id}/`, payload, { headers: { 'Content-Type': 'application/json' } });
              showToast(`Invoice #${editingInvoice.invoice_number} updated successfully!`, 'success');
              closeEditInvoiceModal();
              fetchInvoices();
            } catch (error) {
              console.error('Error updating invoice:', error.response ? error.response.data : error);
              let errorMessage = 'Please check your input.';
              if (error.response?.data) {
                const errorData = error.response.data;
                const specificErrors = Object.entries(errorData).map(([key, value]) => {
                  const messages = Array.isArray(value) ? value.join(', ') : String(value);
                  return `${key}: ${messages}`;
                }).join('; ');
                if (specificErrors) {
                  errorMessage = specificErrors;
                }
              }
              showToast(`Error updating invoice: ${errorMessage}`, 'danger');
            } finally {
              setIsSaving(false);
            }
          }}
        />
      )}

      <InvoiceDetails
        selectedInvoiceForDetails={selectedInvoiceForDetails}
        isLoadingDetails={isLoadingDetails}
        closeDetailsModal={closeDetailsModal}
        detailsTab={detailsTab}
        setDetailsTab={setDetailsTab}
        clients={clients}
        cases={cases}
        openRecordPaymentModal={openRecordPaymentModal}
        openWriteOffModal={openWriteOffModal}
        openCreditNoteModal={openCreditNoteModal}
        openRefundModal={openRefundModal}
        openEditInvoiceModal={openEditInvoiceModal}
        authAxios={authAxios}
        showToast={showToast}
        setSelectedInvoiceForDetails={setSelectedInvoiceForDetails}
      />

      {/* Journal modal (payments and write-offs) */}
      {showJournalModal && journalInvoice && (
        <InvoiceJournalModal
          open={showJournalModal}
          onClose={() => setShowJournalModal(false)}
          onSaved={async () => {
            setShowJournalModal(false);
            await fetchInvoices();
            if (selectedInvoiceForDetails && selectedInvoiceForDetails.id === journalInvoice.id) {
              openDetailsModal(journalInvoice);
            }
            showToast('Journal recorded', 'success');
          }}
          invoiceId={journalInvoice.id}
          authAxios={authAxios}
          mode={journalMode}
          maxAmount={journalMode === 'refund' ? journalInvoice.amount_paid : journalInvoice.balance_due}
        />
      )}
      </>
      )}

      {/* Tax Summary Tab Content */}
      {activeTab === 'tax' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Tax Summary</h3>
                <p className="text-sm text-gray-600">VAT (output vs input) and SAT readiness.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600">Start</label>
                  <input
                    type="date"
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                    value={taxStartDate}
                    onChange={(e) => setTaxStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">End</label>
                  <input
                    type="date"
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                    value={taxEndDate}
                    onChange={(e) => setTaxEndDate(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    className="w-full bg-indigo-600 text-white px-3 py-2 rounded-md text-sm hover:bg-indigo-700"
                    onClick={() => {
                      fetchTaxReport();
                      fetchWhtbPlusReport();
                    }}
                    disabled={taxLoading || whtbPlusLoading}
                  >
                    {(taxLoading || whtbPlusLoading) ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              Penalty reminder: SAT installment underpayments can attract about 1.5% monthly penalty (per API hint). Always confirm current BURS rules.
            </div>

            {taxError && <div className="mt-3 text-sm text-red-600">{taxError}</div>}
            {whtbPlusError && <div className="mt-3 text-sm text-red-600">{whtbPlusError}</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-sm text-gray-600">VAT Collected (Output)</div>
              <div className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(taxReport?.vat_collected)}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-sm text-gray-600">VAT Paid (Input)</div>
              <div className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(taxReport?.vat_paid)}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-sm text-gray-600">VAT Payable</div>
              <div className="text-2xl font-bold text-indigo-700 mt-2">{formatCurrency(taxReport?.vat_payable)}</div>
              {parseFloat(taxReport?.vat_refund_due || 0) > 0 && (
                <div className="text-sm text-green-600 mt-1 font-medium">Refund due: {formatCurrency(taxReport.vat_refund_due)}</div>
              )}
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-sm text-gray-600">SAT Eligible</div>
              <div className={`text-2xl font-bold mt-2 ${taxReport?.sat_eligible ? 'text-red-600' : 'text-green-600'}`}>
                {taxReport?.sat_eligible ? 'Yes' : 'No'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Threshold: {formatCurrency(taxReport?.sat_threshold)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-lg font-semibold text-gray-800">Turnover (Excl. Group K)</h4>
              <div className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(taxReport?.turnover_excl_group_k)}</div>
              <p className="text-xs text-gray-500 mt-1">Used for clean audit separation of disbursements.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="text-lg font-semibold text-gray-800">Disbursements (Group K)</h4>
              <div className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(taxReport?.disbursements_group_k)}</div>
              <p className="text-xs text-gray-500 mt-1">Reported separately and excluded from VAT base.</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-gray-800">Upcoming SAT Deadlines</h4>
            <div className="mt-3">
              {(taxReport?.upcoming_sat_deadlines || []).length === 0 ? (
                <div className="text-sm text-gray-500">No upcoming deadlines available.</div>
              ) : (
                <ul className="space-y-2">
                  {(taxReport?.upcoming_sat_deadlines || []).map((d, idx) => (
                    <li key={idx} className="flex items-center justify-between border border-gray-200 rounded-md p-3">
                      <div className="text-sm text-gray-800">Due: <span className="font-medium">{d.due_date}</span></div>
                      <div className="text-xs text-gray-600">Penalty: {d.penalty_rate_monthly}% / month</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-gray-800">Withholding Tax (WHTB+)</h4>
                <p className="text-xs text-gray-500 mt-1">Based on paid expenses with WHT.</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="bg-gray-100 text-gray-800 px-3 py-2 rounded-md text-sm hover:bg-gray-200"
                  onClick={fetchWhtbPlusReport}
                  disabled={whtbPlusLoading}
                >
                  {whtbPlusLoading ? 'Loading...' : 'Refresh WHT'}
                </button>
                <button
                  type="button"
                  className="bg-indigo-600 text-white px-3 py-2 rounded-md text-sm hover:bg-indigo-700"
                  onClick={downloadWhtbPlusPdf}
                  disabled={whtbPlusLoading}
                >
                  Download WHTB+ PDF
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-5">
              <div className="bg-gray-50 p-4 rounded-md border">
                <div className="text-sm text-gray-600">Total Gross</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(whtbPlusReport?.total_gross)}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <div className="text-sm text-gray-600">Total WHT</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(whtbPlusReport?.total_wht)}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <div className="text-sm text-gray-600">Total Net</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(whtbPlusReport?.total_net)}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <div className="text-sm text-gray-600">Items</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{(whtbPlusReport?.items || []).length}</div>
              </div>
            </div>

            <div className="mt-5">
              {(whtbPlusReport?.items || []).length === 0 ? (
                <div className="text-sm text-gray-500">No WHT items found for the selected period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gross</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">WHT</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Net</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(whtbPlusReport?.items || []).slice(0, 50).map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-2 text-sm text-gray-700">{row.payment_date || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <div className="font-medium">{row.vendor_name || '-'}</div>
                            <div className="text-xs text-gray-500">TIN: {row.vendor_tin || '-'}</div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(row.gross_amount)}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{row.wht_rate_percent}%</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(row.wht_amount)}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(row.net_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(whtbPlusReport?.items || []).length > 50 && (
                    <div className="text-xs text-gray-500 mt-2">Showing first 50 items. Use the PDF export for the full list.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aged Receivables Tab Content */}
      {activeTab === 'receivables' && (
        <AgedReceivables
          authAxios={authAxios}
          showToast={showToast}
          invoices={invoices}
          clients={clients}
        />
      )}

      {/* Cash Transactions Report Tab Content */}
      {activeTab === 'cash-report' && (
        <CashTransactionsReport authAxios={authAxios} showToast={showToast} />
      )}
    </div>
  );
}

export default BillingManagement;
